import { Component } from '@theme/component';
import { StandardEvents, ProductSelectEvent } from '@shopify/events';

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(StandardEvents.productSelect, this.#handleProductSelect, { signal });
    if (this.dataset.hoverZoom === 'true') {
      this.addEventListener('pointermove', this.#handleHoverZoom, { signal });
      this.addEventListener('pointerleave', this.#resetHoverZoom, { signal });
    }

    this.#preloadVariantMedia();
  }

  #controller = new AbortController();
  #variantSwitchToken = 0;

  #preloadVariantMedia() {
    const preload = () => {
      if (!this.isConnected) return;
      const template = this.querySelector('template[data-variant-media-cache]');
      if (!(template instanceof HTMLTemplateElement)) return;

      template.content.querySelectorAll('[data-main-src]').forEach((source) => {
        if (!(source instanceof HTMLElement) || !source.dataset.mainSrc) return;
        const image = new Image();
        image.decoding = 'async';
        image.src = source.dataset.mainSrc;
      });
    };

    if ('requestIdleCallback' in window) {
      window.requestIdleCallback(preload, { timeout: 1500 });
    } else {
      window.setTimeout(preload, 600);
    }
  }

  /** @param {string | undefined} variantId */
  #switchVariantMediaImmediately(variantId) {
    const token = ++this.#variantSwitchToken;
    if (!variantId) return Promise.resolve(false);

    const template = this.querySelector('template[data-variant-media-cache]');
    if (!(template instanceof HTMLTemplateElement)) return Promise.resolve(false);

    const source = template.content.querySelector(`[data-variant-id="${CSS.escape(String(variantId))}"]`);
    const currentImage = this.querySelector(
      'slideshow-slide[aria-hidden="false"] .product-media__image, slideshow-slide .product-media__image'
    );
    if (!(source instanceof HTMLImageElement) || !(currentImage instanceof HTMLImageElement)) {
      return Promise.resolve(false);
    }

    const mainSrc = source.dataset.mainSrc;
    if (!mainSrc) return Promise.resolve(false);

    return new Promise((resolve) => {
      const candidate = new Image();
      let settled = false;
      const finish = (result) => {
        if (settled) return;
        settled = true;
        resolve(result);
      };
      const apply = () => {
        if (token !== this.#variantSwitchToken || !this.isConnected) {
          finish(true);
          return;
        }

        currentImage.src = mainSrc;
        if (source.dataset.srcset) currentImage.srcset = source.dataset.srcset;
        if (source.alt) currentImage.alt = source.alt;
        if (source.dataset.highResSrc) currentImage.dataset.maxResolution = source.dataset.highResSrc;

        const productMedia = currentImage.closest('.product-media');
        if (productMedia instanceof HTMLElement && source.dataset.mediaId) {
          productMedia.dataset.mediaId = source.dataset.mediaId;
        }

        const mediaContainer = currentImage.closest('.product-media-container');
        if (mediaContainer instanceof HTMLElement && source.dataset.aspectRatio) {
          mediaContainer.style.setProperty('--media-preview-ratio', source.dataset.aspectRatio);
        }

        const firstThumbnail = this.querySelector('.slideshow-controls__thumbnail img');
        if (firstThumbnail instanceof HTMLImageElement && source.dataset.thumbnailSrc) {
          firstThumbnail.src = source.dataset.thumbnailSrc;
          firstThumbnail.removeAttribute('srcset');
          if (source.alt) firstThumbnail.alt = source.alt;
        }

        this.removeAttribute('data-variant-image-loading');
        finish(true);
      };

      candidate.decoding = 'async';
      candidate.addEventListener('load', apply, { once: true });
      candidate.addEventListener(
        'error',
        () => {
          this.removeAttribute('data-variant-image-loading');
          finish(false);
        },
        { once: true }
      );
      this.setAttribute('data-variant-image-loading', '');
      candidate.src = mainSrc;
      if (candidate.complete && candidate.naturalWidth > 0) apply();
      window.setTimeout(() => finish(false), 1600);
    });
  }

  #handleHoverZoom = (event) => {
    if (event.pointerType === 'touch' || !window.matchMedia('(hover: hover) and (pointer: fine)').matches) return;

    const target = event.target instanceof Element ? event.target : null;
    const media = target?.closest('.product-media-container--hover-zoomable');
    if (!(media instanceof HTMLElement) || !this.contains(media)) return;

    const rect = media.getBoundingClientRect();
    if (!rect.width || !rect.height) return;

    const x = Math.min(100, Math.max(0, ((event.clientX - rect.left) / rect.width) * 100));
    const y = Math.min(100, Math.max(0, ((event.clientY - rect.top) / rect.height) * 100));

    this.querySelectorAll('[data-hover-zoom-active]').forEach((item) => {
      if (item !== media) item.removeAttribute('data-hover-zoom-active');
    });
    media.style.setProperty('--hover-zoom-x', `${x}%`);
    media.style.setProperty('--hover-zoom-y', `${y}%`);
    media.setAttribute('data-hover-zoom-active', '');
  };

  #resetHoverZoom = () => {
    this.querySelectorAll('[data-hover-zoom-active]').forEach((media) => {
      media.removeAttribute('data-hover-zoom-active');
      if (media instanceof HTMLElement) {
        media.style.removeProperty('--hover-zoom-x');
        media.style.removeProperty('--hover-zoom-y');
      }
    });
  };

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
  }

  /**
   * Handles a product select event by replacing the current media gallery with a new one.
   *
   * @param {ProductSelectEvent} event - The product select event.
   */
  #handleProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

    const quickSwitch = this.#switchVariantMediaImmediately(event.detail?.variantId);

    event.promise
      .then(async ({ detail }) => {
        if (await quickSwitch) return;
        if (!detail?.html) return;

        const { html } = detail;
        const newMediaGallery = html.querySelector('media-gallery');
        if (!newMediaGallery) return;

        this.replaceWith(newMediaGallery);
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[media-gallery] Event promise rejected:', error);
      });
  };

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
